import axios from 'axios'

const API_URL = process.env.REACT_APP_API_URL
const MODELS_URL = `${API_URL}/modelhighlights/`

// ACTION TYPES
const name = 'data'
export const actionTypes = {
  LOAD_SAMPLE_DATA: `${name}:load_sample_data`,
  LOAD_PERSONAL_STRATEGIES: `${name}:get_personal_strategies`,
  LOAD_MODEL_STRATEGIES: `${name}:get_model_strategies`,
  DELETE_A_STRATEGY: `${name}:delete_a_stategy`,
  SHOW_A_STRATEGY: `${name}:show_a_stategy`,
  SHOW_ALL_STRATEGIES: `${name}:show_all_stategies`,
  SHOW_STRATEGY_EDITOR: `${name}:show_strategy_editor`,
  CLOSE_STRATEGY_EDITOR: `${name}:close_strategy_editor`,
  UPDATE_STRATEGY: `${name}:update_strategy`,
  LOAD_TYPEAHEAD_SUGGESTIONS: `${name}:'load_typeahead_suggestions`,
  SAVING_NEW_STRATEGY_IN_PROGRESS: `${name}:'saving_new_strategy_in_progress`,
  SAVING_STRATEGY_IN_PROGRESS: `${name}:'saving_strategy_in_progress`,
  SAVING_STRATEGY_COMPLETE: `${name}:'saving_strategy_complete`,
  SAVE_NEW_STRATEGY: `${name}:'save_new_strategy`,
  LOAD_BENCHMARKS: `${name}:'load_benchmarks`,
  HANDLE_CHECKBOX: `${name}:'handle_checkbox`,
  PERSONAL_STRATEGIES_ERROR: `${name}:personal_strategies_error`,
  PERSONAL_STRATEGIES_LOADING: `${name}:personal_strategies_loading`,
  MODEL_STRATEGIES_ERROR: `${name}:model_strategies_error`,
  MODEL_STRATEGIES_LOADING: `${name}:model_strategies_loading`
}

// Load all personal strategies for a user
export function loadPersonalStrategies() {
  return (dispatch, getState) => {
    let params = getState().params

    if (params.token !== 'undefined') {
      dispatch({ type: actionTypes.PERSONAL_STRATEGIES_LOADING, value: true })
      dispatch({ type: actionTypes.PERSONAL_STRATEGIES_ERROR, value: false })
      const clientEmail = encodeURIComponent(params.advisorEmail)
      const baseUrl = `${process.env.REACT_APP_API_URL}/personalstrategies/list-v2/`
      const urls = [
        `${baseUrl}?portHubType=user-custom-model&clientEmail=${clientEmail}`,
        `${baseUrl}?portHubType=user-model&clientEmail=${clientEmail}`
      ]
      const request = {
        method: 'GET',
        headers: {
          Authorization: params.token && `Token ${params.token}`
        }
      }
      return Promise.all(urls.map(url => axios({ ...request, url })))
        .then(responses => {
          const mergedData = responses.flatMap(response =>
            Array.isArray(response.data) ? response.data : []
          )
          const sortedData = mergedData.sort((a, b) => {
            const aTime = Date.parse(a.createDate) || 0
            const bTime = Date.parse(b.createDate) || 0
            return bTime - aTime
          })

          dispatch({ type: actionTypes.LOAD_PERSONAL_STRATEGIES, value: sortedData })
          dispatch({ type: actionTypes.PERSONAL_STRATEGIES_ERROR, value: false })
          dispatch({ type: actionTypes.PERSONAL_STRATEGIES_LOADING, value: false })
        })
        .catch(() => {
          dispatch({ type: actionTypes.LOAD_PERSONAL_STRATEGIES, value: [] })
          dispatch({ type: actionTypes.PERSONAL_STRATEGIES_ERROR, value: true })
          dispatch({ type: actionTypes.PERSONAL_STRATEGIES_LOADING, value: false })
        })
    } // If token isn't valid then don't load anything
    else {
      dispatch({ type: actionTypes.LOAD_PERSONAL_STRATEGIES, value: [] })
      dispatch({ type: actionTypes.PERSONAL_STRATEGIES_ERROR, value: false })
      dispatch({ type: actionTypes.PERSONAL_STRATEGIES_LOADING, value: false })
    }
  }
}

export function deletePersonalStrategy(idToDelete) {
  return (dispatch, getState) => {
    // Let the StrategyList know that one of the strategies is being deleted
    dispatch({ type: actionTypes.DELETE_A_STRATEGY, value: idToDelete })
    dispatch({ type: actionTypes.SAVING_STRATEGY_IN_PROGRESS, value: idToDelete })

    let params = getState().params

    const body = {
      clientEmail: params.advisorEmail,
      isDeleted: true
    }

    if (params.token !== 'undefined' && idToDelete) {
      const request = {
        method: 'PUT',
        url: `${process.env.REACT_APP_API_URL}/personalstrategies/${idToDelete}/`,
        data: body,
        headers: {
          'Content-Type': 'application/json',
          Authorization: params.token && `Token ${params.token}`
        }
      }
      return axios(request)
        .then(response => {
          dispatch({ type: actionTypes.DELETE_A_STRATEGY, value: idToDelete })
          dispatch({ type: actionTypes.SHOW_ALL_STRATEGIES })
          dispatch({ type: actionTypes.SAVING_STRATEGY_COMPLETE })
        })
        .catch(error => {
          console.error(error)
        })
    }
  }
}

// Show a given personal strategy by id, and hide all the rest in the StrategyList
export function showPersonalStrategy(idToShow) {
  return (dispatch, getState) => {
    dispatch({ type: actionTypes.SHOW_A_STRATEGY, value: idToShow })
  }
}

// Show all strategies in the StrategyList
export function showAllStrategies() {
  return (dispatch, getState) => {
    dispatch({ type: actionTypes.SHOW_ALL_STRATEGIES })
  }
}

// Show the Strategy editor. May be called from the StrategyList
// or an individual StrategyRow
export function showStrategyEditor(personalStrategy) {
  return (dispatch, getState) => {
    dispatch({ type: actionTypes.SHOW_STRATEGY_EDITOR, value: personalStrategy })
  }
}

export function handleCheckbox(personalStrategy) {
  return (dispatch, getState) => {
    dispatch({ type: actionTypes.HANDLE_CHECKBOX, value: personalStrategy })
  }
}

// Close the StrategyEditor
export function closeStrategyEditor() {
  return (dispatch, getState) => {
    dispatch({ type: actionTypes.CLOSE_STRATEGY_EDITOR })
  }
}

// Update the Personal Strategy's details (e.g., name)
export function saveNewStrategy(fieldsToUpdate) {
  return (dispatch, getState) => {
    // Let the StrategyList know that a new strategy is being saved
    dispatch({ type: actionTypes.SAVING_NEW_STRATEGY_IN_PROGRESS, value: fieldsToUpdate })

    let params = getState().params
    let body = {
      clientEmail: params.advisorEmail,
      wtRegion: params.wtRegion,
      name: fieldsToUpdate.name,
      holdings: fieldsToUpdate.holdings,
      totalValue: fieldsToUpdate.totalValue,
      periodicity: fieldsToUpdate.periodicity
    }

    // Add a benchmark if one has been added
    if (fieldsToUpdate.benchmarkExternalID) {
      body.benchmarks = [
        {
          externalID: fieldsToUpdate.benchmarkExternalID
        }
      ]
    }

    if (params.token !== 'undefined') {
      const request = {
        method: 'POST',
        url: `${process.env.REACT_APP_API_URL}/personalstrategies/`,
        data: body,
        headers: {
          'Content-Type': 'application/json',
          Authorization: params.token && `Token ${params.token}`
        }
      }
      console.log('request', request)
      return axios(request)
        .then(response => {
          dispatch({ type: actionTypes.SAVE_NEW_STRATEGY, value: response.data })
          dispatch({ type: actionTypes.SAVING_STRATEGY_COMPLETE })

          // Update the Hash URL to force dpd to reload
          //window.location = '#myportfolios&reloadhistory=true'

          // Optimize the strategy now it's been created
          optimizePersonalStrategy(dispatch, params, response.data.id)
        })
        .catch(error => {
          console.error(error)
        })
    }
  }
}

// Update the Personal Strategy's details (e.g., name)
export function updateStategyDetails(fieldsToUpdate) {
  return (dispatch, getState) => {
    // Let the StrategyList know that one of the strategies is being saved
    dispatch({ type: actionTypes.SAVING_STRATEGY_IN_PROGRESS, value: fieldsToUpdate.id })

    let params = getState().params

    let body = {
      clientEmail: params.advisorEmail,
      name: fieldsToUpdate.name,
      totalValue: fieldsToUpdate.totalValue,
      periodicity: fieldsToUpdate.periodicity
    }

    // Check if holdings were updated
    if (fieldsToUpdate.holdings) {
      body.holdings = fieldsToUpdate.holdings
    }

    // Add a benchmark if one has been added, otherwise force it to be blanked out with empty array
    if (fieldsToUpdate.benchmarkExternalID) {
      body.benchmarks = [
        {
          externalID: fieldsToUpdate.benchmarkExternalID
        }
      ]
    } else {
      body.benchmarks = []
    }

    if (params.token !== 'undefined' && fieldsToUpdate.id) {
      const request = {
        method: 'PUT',
        url: `${process.env.REACT_APP_API_URL}/personalstrategies/${fieldsToUpdate.id}/`,
        data: body,
        headers: {
          'Content-Type': 'application/json',
          Authorization: params.token && `Token ${params.token}`
        }
      }
      return axios(request)
        .then(response => {
          dispatch({ type: actionTypes.UPDATE_STRATEGY, value: response.data })
          dispatch({ type: actionTypes.SAVING_STRATEGY_COMPLETE })

          // Update the Hash URL to force dpd to reload
          //window.location = '#myportfolios&reloadhistory=true'

          // Check if holdings were updated
          if (fieldsToUpdate.holdings) {
            // Optimize the strategy now it's been created
            optimizePersonalStrategy(dispatch, params, fieldsToUpdate.id)
          }
        })
        .catch(error => {
          console.error(error)
        })
    }
  }
}

// Send a strategy to DPD to optimize (not designed to be used outside of an existing action)
function optimizePersonalStrategy(dispatch, params, idToOptimize) {
  const body = {
    clientEmail: params.advisorEmail
  }

  if (params.token !== 'undefined' && idToOptimize) {
    const request = {
      method: 'POST',
      url: `${process.env.REACT_APP_API_URL}/personalstrategies/${idToOptimize}/optimize/`,
      data: body,
      headers: {
        'Content-Type': 'application/json',
        Authorization: params.token && `Token ${params.token}`
      }
    }
    return axios(request)
      .then(response => {
        dispatch({ type: actionTypes.UPDATE_STRATEGY, value: response.data })
      })
      .catch(error => {
        console.error(error)
      })
  }
}

// Create a new strategy out of a portfolio from DPD
export function savePortfolioAsNewStrategy(portfolioId, portfolioName) {
  return (dispatch, getState) => {
    let params = getState().params

    let body = {
      clientEmail: params.advisorEmail,
      portfolioId: portfolioId,
      portfolioName: portfolioName
    }

    if (params.token !== 'undefined' && portfolioId) {
      const request = {
        method: 'POST',
        url: `${process.env.REACT_APP_API_URL}/personalstrategies/create_from_portfolio/`,
        data: body,
        headers: {
          'Content-Type': 'application/json',
          Authorization: params.token && `Token ${params.token}`
        }
      }
      return axios(request)
        .then(response => {
          dispatch({ type: actionTypes.SAVE_NEW_STRATEGY, value: response.data })

          // Update the Hash URL to let DPD know saving has finished
          window.location = '#dpd&savedToMyPortfolios=' + portfolioId + '&newMyPortfolio=' + response.data.id
        })
        .catch(error => {
          console.error(error)
        })
    }
  }
}

/// Get all the benchmarks from the database
export function getBenchmarks() {
  return (dispatch, getState) => {
    let params = getState().params

    if (params.token !== undefined) {
      const request = {
        method: 'GET',
        url: `${process.env.REACT_APP_DPD_URL}/api/benchmarks/all/`,
        params: {
          clientEmail: params.advisorEmail,
          entityCCY: params.entityCCY,
          wtRegion: params.wtRegion
        },
        headers: {
          Authorization: params.token && `Token ${params.token}`
        }
      }
      return axios(request)
        .then(response => {
          if (response.status === 200) {
            dispatch({ type: actionTypes.LOAD_BENCHMARKS, value: response.data })
          }
        })
        .catch(error => {
          console.error(error)
        })
    }
  }
}


export function loadModelStrategies(forceRefresh = false) {
  return (dispatch, getState) => {
    const state = getState()
    const params = state.params
    const hasValidToken = Boolean(params.token && params.token !== 'undefined')
    const modelStrategies = state.data?.modelStrategies
    const modelStrategiesLoading = state.data?.modelStrategiesLoading
    const modelStrategiesLoaded = state.data?.modelStrategiesLoaded

    // Avoid re-fetching when models are already loaded or currently loading.
    if (!forceRefresh) {
      if (modelStrategiesLoaded) {
        return Promise.resolve(modelStrategies)
      }
      if (modelStrategiesLoading) {
        return Promise.resolve(modelStrategies)
      }
      if (Array.isArray(modelStrategies) && modelStrategies.length > 0) {
        return Promise.resolve(modelStrategies)
      }
    }

    if (hasValidToken) {
      dispatch({ type: actionTypes.MODEL_STRATEGIES_LOADING, value: true })
      dispatch({ type: actionTypes.MODEL_STRATEGIES_ERROR, value: false })
      const request = {
        method: 'GET',
        url: `${MODELS_URL}models`,
        params: {
          clientEmail: params.advisorEmail,
        },
        headers: {
          Authorization: `Token ${params.token}`
        }
      }
      return axios(request)
        .then(response => {
          if (response.status === 200) {
            dispatch({ type: actionTypes.LOAD_MODEL_STRATEGIES, value: response.data.filter(x => !x.ticker.includes('CRP'))})
          }
          dispatch({ type: actionTypes.MODEL_STRATEGIES_ERROR, value: false })
          dispatch({ type: actionTypes.MODEL_STRATEGIES_LOADING, value: false })
        })
        .catch(() => {
          dispatch({ type: actionTypes.LOAD_MODEL_STRATEGIES, value: [] })
          dispatch({ type: actionTypes.MODEL_STRATEGIES_ERROR, value: true })
          dispatch({ type: actionTypes.MODEL_STRATEGIES_LOADING, value: false })
        })
    }
  }
}
